import { ReactNode } from "react";

interface HeroSectionProps {
  title: string;
  description: string;
  icons?: [ReactNode, ReactNode];
  className?: string;
  /** Optional brand mark (e.g. logo) shown above the title */
  brandImage?: string;
  brandImageAlt?: string;
}

/** Scattered sparkle dots for hero background - positions as [cx%, cy%, r, twinkle?] */
const SPARKLES: [number, number, number, boolean][] = [
  [10, 15, 1, true], [25, 8, 1.5, false], [42, 22, 1, true], [58, 12, 1, false], [75, 18, 1.5, true], [88, 25, 1, true],
  [8, 45, 1, false], [22, 55, 1.5, true], [38, 48, 1, false], [55, 52, 1, true], [72, 42, 1.5, false], [92, 55, 1, true],
  [15, 78, 1, true], [35, 72, 1, false], [50, 80, 1.5, true], [68, 75, 1, true], [85, 82, 1, false],
  [5, 35, 1, false], [95, 38, 1.5, true], [18, 88, 1, true], [82, 8, 1, false],
];

export const HeroSection = ({
  title,
  description,
  icons,
  className = "",
  brandImage,
  brandImageAlt = "",
}: HeroSectionProps) => {
  const sparkleLayer = (
    <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none rounded-2xl" aria-hidden>
      <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="hero-sparkle-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="0.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {SPARKLES.map(([cx, cy, r, twinkle], i) => (
          <circle
            key={i}
            cx={`${cx}%`}
            cy={`${cy}%`}
            r={r}
            fill="rgba(255,255,255,0.5)"
            filter="url(#hero-sparkle-glow)"
            className={twinkle ? "animate-hero-twinkle" : ""}
            style={twinkle ? { animationDelay: `${i * 0.15}s` } : undefined}
          />
        ))}
      </svg>
    </div>
  );

  if (icons) return (
    <div className={`text-center mb-4 ${className}`}>
      <div className="relative w-full h-full pt-6 pb-5 px-8 min-h-[140px]">
        {sparkleLayer}
        <div className="relative z-10">
          <span className="flex items-center justify-center mb-3 gap-4">
            {icons[0] && icons[0]}
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#3966be] via-[#D216CC] to-[#ed658e] bg-clip-text text-transparent">
              {title}
            </h1>
            {icons[1] && icons[1]}
          </span>
          <p className="text-base lg:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            {description}
          </p>
        </div>
      </div>
    </div>
  )

  return (
    <div className={`text-center mb-4 ${className}`}>
      <div className="relative w-full h-full pt-6 pb-5 px-8">
        {sparkleLayer}
        <div className="relative z-10">
          {brandImage ? (
            <img
              src={brandImage}
              alt={brandImageAlt}
              className="mx-auto mb-4 h-14 w-auto max-w-[200px] object-contain"
            />
          ) : null}
          <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(to right, #4A7AE2 0%, #6D5BF7 18%, #8B45F5 32%, #B52BE0 48%, #D216CC 62%, #E8408A 80%, #FB6B96 100%)" }}>
            {title}
          </h1>
          <p className="text-base lg:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed mt-2">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
